import { useState, useEffect, useCallback, useRef } from 'react';
import { saveTimerState, getTimerState, clearTimerState, saveSession, getActiveSession } from '../utils/db';
import { generateId, getToday } from '../utils/timeUtils';
import { calculateSessionXP } from '../utils/xpUtils';

/**
 * Timer states:
 * - idle: No active session
 * - running: Timer counting down, plant growing
 * - break: Rest period between sessions
 * - completed: Session finished successfully
 * - killed: Plant wilted (user left)
 */

const SHORT_BREAK_MS = 5 * 60 * 1000; // 5 minutes
const LONG_BREAK_MS = 15 * 60 * 1000; // 15 minutes
const LONG_BREAK_INTERVAL = 4; // Every 4th session

export function useTimer() {
  const [state, setState] = useState('idle'); // idle | running | break | completed | killed
  const [sessionId, setSessionId] = useState(null);
  const [durationMs, setDurationMs] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [pausedElapsed, setPausedElapsed] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [sessionData, setSessionData] = useState(null); // completed session data
  const [breakDurationMs, setBreakDurationMs] = useState(0);
  const [breakStartTime, setBreakStartTime] = useState(null);
  const [breakRemaining, setBreakRemaining] = useState(0);
  const [isLongBreak, setIsLongBreak] = useState(false);
  const [consecutiveSessions, setConsecutiveSessions] = useState(0);
  const rafRef = useRef(null);
  const endTimeoutRef = useRef(null);
  const breakRafRef = useRef(null);
  const breakTimeoutRef = useRef(null);

  // Calculate elapsed time
  const getElapsed = useCallback(() => {
    if (!startTime) return pausedElapsed;
    return pausedElapsed + (Date.now() - startTime);
  }, [startTime, pausedElapsed]);

  // Progress from 0 to 1
  const progress = durationMs > 0 ? Math.min(getElapsed() / durationMs, 1) : 0;

  // Tick loop using requestAnimationFrame
  useEffect(() => {
    if (state !== 'running') {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    let lastUpdate = 0;

    function tick(timestamp) {
      // Throttle display updates to ~4 per second when not actively viewing
      if (timestamp - lastUpdate > 250) {
        const elapsed = getElapsed();
        const rem = Math.max(durationMs - elapsed, 0);
        setRemaining(rem);
        lastUpdate = timestamp;

        if (rem <= 0) {
          completeSession();
          return;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);

    // Also set a setTimeout for the exact end time (survives rAF throttling)
    const timeUntilEnd = durationMs - getElapsed();
    if (timeUntilEnd > 0) {
      endTimeoutRef.current = setTimeout(() => {
        completeSession();
      }, timeUntilEnd);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (endTimeoutRef.current) clearTimeout(endTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, startTime, durationMs]);

  // Recover from crash on mount
  useEffect(() => {
    async function recover() {
      const timerState = await getTimerState();
      if (!timerState) return;

      const elapsed = timerState.pausedElapsed + (Date.now() - timerState.startTime);

      if (elapsed >= timerState.durationMs) {
        // Session finished while app was closed — auto-complete
        const session = {
          id: timerState.sessionId,
          startedAt: new Date(timerState.startTime).toISOString(),
          endedAt: new Date(timerState.startTime + timerState.durationMs).toISOString(),
          durationMinutes: Math.round(timerState.durationMs / 60000),
          completed: true,
          killed: false,
          plantType: timerState.plantType || 'roma',
          tag: timerState.tag || 'work',
          date: timerState.date,
          xpEarned: calculateSessionXP(Math.round(timerState.durationMs / 60000)),
        };
        await saveSession(session);
        await clearTimerState();
        setSessionData(session);
        setState('completed');
      } else {
        // Resume the timer
        setSessionId(timerState.sessionId);
        setDurationMs(timerState.durationMs);
        setStartTime(Date.now());
        setPausedElapsed(elapsed);
        setRemaining(timerState.durationMs - elapsed);
        setState('running');
      }
    }

    recover();
  }, []);

  // Start a new session
  const startSession = useCallback(async (minutes, tag = 'work', plantType = 'roma') => {
    const id = generateId();
    const duration = minutes * 60 * 1000;
    const now = Date.now();

    setSessionId(id);
    setDurationMs(duration);
    setStartTime(now);
    setPausedElapsed(0);
    setRemaining(duration);
    setSessionData(null);
    setState('running');

    // Persist to IndexedDB for crash recovery
    await saveTimerState({
      sessionId: id,
      durationMs: duration,
      startTime: now,
      pausedElapsed: 0,
      plantType,
      tag,
      date: getToday(),
    });

    // Save session record as active
    await saveSession({
      id,
      startedAt: new Date(now).toISOString(),
      endedAt: null,
      durationMinutes: minutes,
      completed: false,
      killed: false,
      plantType,
      tag,
      date: getToday(),
      status: 'active',
      xpEarned: 0,
    });

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Complete session successfully
  const completeSession = useCallback(async () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (endTimeoutRef.current) clearTimeout(endTimeoutRef.current);

    const activeSession = await getActiveSession();
    if (!activeSession) {
      setState('idle');
      return;
    }

    const xpEarned = calculateSessionXP(activeSession.durationMinutes);
    const completedSession = {
      ...activeSession,
      endedAt: new Date().toISOString(),
      completed: true,
      status: 'completed',
      xpEarned,
    };

    await saveSession(completedSession);
    await clearTimerState();

    setSessionData(completedSession);
    setRemaining(0);
    setState('completed');

    // Browser notification
    if (Notification.permission === 'granted') {
      try {
        new Notification('Session Complete!', {
          body: `Your ${activeSession.plantType || 'roma'} plant is fully grown. +${xpEarned} XP`,
          icon: '/logo192.png',
          tag: 'session-complete',
        });
      } catch {
        // ignore
      }
    }
  }, []);

  // Wilt plant. reason='wilt' = grace period expired (user left); 'giveup' = user tapped Give up.
  // Old records without `reason` default to 'wilt' on read.
  const wiltPlant = useCallback(async (reason = 'wilt') => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (endTimeoutRef.current) clearTimeout(endTimeoutRef.current);

    const activeSession = await getActiveSession();
    if (!activeSession) {
      setState('idle');
      return;
    }

    const killedSession = {
      ...activeSession,
      endedAt: new Date().toISOString(),
      killed: true,
      completed: false,
      status: 'killed',
      reason,
      xpEarned: 0,
    };

    await saveSession(killedSession);
    await clearTimerState();

    setSessionData(killedSession);
    setState('killed');
  }, []);

  // Break tick loop
  useEffect(() => {
    if (state !== 'break') {
      if (breakRafRef.current) cancelAnimationFrame(breakRafRef.current);
      return;
    }

    let lastUpdate = 0;

    function tick(timestamp) {
      if (timestamp - lastUpdate > 250) {
        const elapsed = Date.now() - breakStartTime;
        const rem = Math.max(breakDurationMs - elapsed, 0);
        setBreakRemaining(rem);
        lastUpdate = timestamp;

        if (rem <= 0) {
          completeBreak();
          return;
        }
      }
      breakRafRef.current = requestAnimationFrame(tick);
    }

    breakRafRef.current = requestAnimationFrame(tick);

    const timeUntilEnd = breakDurationMs - (Date.now() - breakStartTime);
    if (timeUntilEnd > 0) {
      breakTimeoutRef.current = setTimeout(() => {
        completeBreak();
      }, timeUntilEnd);
    }

    return () => {
      if (breakRafRef.current) cancelAnimationFrame(breakRafRef.current);
      if (breakTimeoutRef.current) clearTimeout(breakTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, breakStartTime, breakDurationMs]);

  // Start a break period
  const startBreak = useCallback(() => {
    const count = consecutiveSessions + 1;
    setConsecutiveSessions(count);
    const long = count % LONG_BREAK_INTERVAL === 0;
    const duration = long ? LONG_BREAK_MS : SHORT_BREAK_MS;
    setIsLongBreak(long);
    setBreakDurationMs(duration);
    setBreakStartTime(Date.now());
    setBreakRemaining(duration);
    setState('break');
  }, [consecutiveSessions]);

  // Complete break naturally
  const completeBreak = useCallback(() => {
    if (breakRafRef.current) cancelAnimationFrame(breakRafRef.current);
    if (breakTimeoutRef.current) clearTimeout(breakTimeoutRef.current);
    setBreakRemaining(0);
    setState('idle');
  }, []);

  // Skip break early
  const skipBreak = useCallback(() => {
    if (breakRafRef.current) cancelAnimationFrame(breakRafRef.current);
    if (breakTimeoutRef.current) clearTimeout(breakTimeoutRef.current);
    setBreakRemaining(0);
    setState('idle');
  }, []);

  // Reset to idle
  const reset = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (endTimeoutRef.current) clearTimeout(endTimeoutRef.current);
    if (breakRafRef.current) cancelAnimationFrame(breakRafRef.current);
    if (breakTimeoutRef.current) clearTimeout(breakTimeoutRef.current);
    setState('idle');
    setSessionId(null);
    setDurationMs(0);
    setStartTime(null);
    setPausedElapsed(0);
    setRemaining(0);
    setSessionData(null);
    setBreakDurationMs(0);
    setBreakStartTime(null);
    setBreakRemaining(0);
  }, []);

  // Give up (voluntary abandon)
  const giveUp = useCallback(async () => {
    await wiltPlant('giveup');
  }, [wiltPlant]);

  return {
    state,
    sessionId,
    remaining,
    durationMs,
    progress,
    sessionData,
    breakRemaining,
    breakDurationMs,
    isLongBreak,
    startSession,
    completeSession,
    wiltPlant,
    giveUp,
    startBreak,
    skipBreak,
    reset,
  };
}
